import React, { useState, useEffect, useContext, useMemo } from "react";
import { Rnd } from "react-rnd";

import { crudeDatabase } from "../data/crudeDatabase";
import { bargeDatabase } from "../data/bargeDatabase";
import { WellContext } from "../context/WellContext";

import "./MDSFaceplate.css";

/* =========================
   STRICT VALUE PARSER
========================= */
function parseValue(val) {

  if (val === null || val === undefined) {
    return { value: "", unit: "" };
  }

  if (typeof val === "object") {

    if (val.value !== undefined || val.unit !== undefined) {

      return {
        value: String(val.value ?? ""),
        unit: String(val.unit ?? "")
      };
    }

    return {
      value: "",
      unit: ""
    };
  }

  const str = String(val);

  const match =
    str.match(/^([0-9.\-\s]+)\s*(.*)$/);

  return {
    value: match ? match[1].trim() : str,
    unit: match ? match[2].trim() : ""
  };
}

/* =========================
   EDITABLE BLOCK
========================= */
function EditableBlock({
title,
data,
isEditing,
setEditableCrude,
path
}) {

if (!data) return null;

/* =========================
SIMPLE VALUE EDIT
========================= */

const handleSimpleChange = (
key,
newValue
) => {


setEditableCrude((prev) => ({
  ...prev,

  [path]: {
    ...prev[path],

    [key]: {
      ...prev[path][key],
      value: newValue
    }
  }
}));

};

/* =========================
NESTED VALUE EDIT
========================= */

const handleNestedChange = (
key,
subKey,
newValue
) => {


setEditableCrude((prev) => ({
  ...prev,

  [path]: {
    ...prev[path],

    [key]: {
      ...prev[path][key],

      [subKey]: {
        ...prev[path][key][subKey],
        value: newValue
      }
    }
  }
}));


};

return (


<div className="mds-section">

  <h3 className="mds-section-title">
    {title}
  </h3>

  <div className="mds-grid">

    {Object.entries(data).map(([key, val]) => {

      const isNested =
        typeof val === "object" &&
        !("value" in val) &&
        !("unit" in val);

      /* =========================
         NESTED BLOCKS
      ========================= */

      if (isNested) {

        return (

          <div
            key={key}
            className="mds-nested-block"
          >

            <div className="mds-nested-title">
              {key}
            </div>

            {Object.entries(val).map(
              ([subKey, subVal]) => {

                const parsed =
                  parseValue(subVal);

                return (

                  <div
                    key={subKey}
                    className="mds-row"
                  >

                    <div className="mds-key">
                      {subKey}
                    </div>

                    <div className="mds-value-wrapper">

                      <div className="mds-value-box">

                        {isEditing ? (

                          <input
                            className="mds-input"

                            value={parsed.value}

                            onChange={(e) =>
                              handleNestedChange(
                                key,
                                subKey,
                                e.target.value
                              )
                            }
                          />

                        ) : (

                          <span className="mds-value-text">
                            {parsed.value}
                          </span>

                        )}

                      </div>

                      <div className="mds-unit-box">
                        {parsed.unit || "-"}
                      </div>

                    </div>

                  </div>

                );
              }
            )}

          </div>
        );
      }

      /* =========================
         NORMAL BLOCKS
      ========================= */

      const parsed = parseValue(val);

      return (

        <div
          key={key}
          className="mds-row"
        >

          <div className="mds-key">
            {val?.name || key}
          </div>

          <div className="mds-value-wrapper">

            <div className="mds-value-box">

              {isEditing ? (

                <input
                  className="mds-input"

                  value={parsed.value}

                  onChange={(e) =>
                    handleSimpleChange(
                      key,
                      e.target.value
                    )
                  }
                />

              ) : (

                <span className="mds-value-text">
                  {parsed.value}
                </span>

              )}

            </div>

            <div className="mds-unit-box">
              {parsed.unit || "-"}
            </div>

          </div>

        </div>
      );
    })}

  </div>

</div>

);
}


/* =========================
   MAIN COMPONENT
========================= */

function MDSFaceplate({

  close = () => {},

  showBargeSelector = true,

  defaultPosition = {
    x: 120,
    y: 80
  },

  // NEW
  tankID = 1,

  storageKey = "TANK1_SELECTED_CRUDE",

  mode = "tank"

}) {


  const { transmitters } =
    useContext(WellContext);

const [selectedCrude, setSelectedCrude] = useState(() => {

    return (
        localStorage.getItem(storageKey) || ""
    );

});

useEffect(() => {

    localStorage.setItem(
        storageKey,
        selectedCrude
    );

}, [selectedCrude, storageKey]);


useEffect(() => {

    if (mode !== "tank") return;

    const crude = crudeDatabase[selectedCrude];

    if (!crude) return;

    localStorage.setItem(

        `CRUDE_TANK_${tankID}`,

        JSON.stringify({

            tankID,

            crudeKey: selectedCrude,

            crudeName: crude.name,

            timestamp: Date.now()

        })

    );

}, [
    selectedCrude,
    tankID,
    mode
]);


const [selectedBarge, setSelectedBarge] = useState(() => {
  return localStorage.getItem("MDS_selectedBarge") || "";
});

useEffect(() => {
  localStorage.setItem("MDS_selectedBarge", selectedBarge);
}, [selectedBarge]);

  const [activeTab, setActiveTab] =
    useState("quantity");

  const [isEditing, setIsEditing] =
    useState(false);

  const [editableCrude, setEditableCrude] =
    useState(null);


 const MDS_WIDTH = 750;
const MDS_HEIGHT = 520;

const clampPosition = (x, y) => {

  const maxX =
    Math.max(
      10,
      window.innerWidth - MDS_WIDTH - 10
    );

  const maxY =
    Math.max(
      10,
      window.innerHeight - MDS_HEIGHT - 10
    );

  return {

    x: Math.min(
      Math.max(x, 10),
      maxX
    ),

    y: Math.min(
      Math.max(y, 10),
      maxY
    )

  };

};

const [faceplatePos, setFaceplatePos] = useState(() => {

  try {

    const saved = JSON.parse(
      localStorage.getItem("MDS_faceplatePos")
    );

    if (saved) {

      return clampPosition(
        saved.x,
        saved.y
      );

    }

  } catch {}

  return clampPosition(
    defaultPosition.x,
    defaultPosition.y
  );

});






useEffect(() => {

  const fixed =
    clampPosition(
      faceplatePos.x,
      faceplatePos.y
    );

  localStorage.setItem(
    "MDS_faceplatePos",
    JSON.stringify(fixed)
  );

}, [faceplatePos]);



  /* =========================================
     BARGE CAPACITY
  ========================================= */

  const bargeCapacity = useMemo(() => {

    return (
      bargeDatabase[selectedBarge]
        ?.capacity?.bbl || 0
    );

  }, [selectedBarge]);

  /* =========================================
     QUANTITY STATES
  ========================================= */

const [loadedBBL, setLoadedBBL] = useState(() => {
  return Number(localStorage.getItem("MDS_loadedBBL")) || 0;
});


/* =========================================
   SAVE LOADED BBL FOR MARINE TERMINAL
========================================= */

const LT_1001_total = bargeCapacity;

/* =========================================
   SIMPLE LOCAL STORAGE SYNC
========================================= */

  useEffect(() => {
    localStorage.setItem("bargeCapacity", bargeCapacity);
    }, [bargeCapacity]); 

  useEffect(() => {
    localStorage.setItem("loadedBBL", loadedBBL);
    }, [loadedBBL]); 



const crude =
  editableCrude ||
  crudeDatabase[selectedCrude];

  
  /* =========================================
     CONVERSIONS
  ========================================= */

  const loadedMetricTonnes =
    loadedBBL * 0.136;

  const loadedLitres =
    loadedBBL * 158.987;

  /* =========================================
     FLOW TOTALIZER PLACEHOLDER
  ========================================= */

  /*
  Replace later with:

  const FIQ_1001 =
    transmitters?.["FIQ_1001"]?.value || 0;
  */

  const FIQ_1001 = 0;

  /* =========================================
     QUANTITY UNLOADED
  ========================================= */

  const quantityUnloaded =
    loadedBBL - FIQ_1001;

  /* =========================================
     LOADED QUANTITY
  ========================================= */

  const crudeLoadedQuantity =
    useMemo(() => {

      if (!selectedBarge) return 0;

      return loadedBBL;

    }, [
      loadedBBL,
      selectedBarge
    ]);

  



  /* =========================================
     SECTION RENDERER
  ========================================= */

  const renderSection = () => {

    if (!crude) {

      return (
        <div className="mds-empty">
          Select Crude Type
        </div>
      );
    }

    switch (activeTab) {

      /* =====================================
         QUANTITY TAB
      ===================================== */

      case "quantity":

        return (
          <div className="mds-section">

            <h3>
              QUANTITY
            </h3>

            <div className="mds-info-card">

              {/* ================= BARGE CAPACITY ================= */}

              <div className="mds-info-row">

                <span>
                  Barge Capacity
                </span>

                <span>
                  {bargeCapacity.toLocaleString()} BBL
                </span>

              </div>

              {/* ================= LOADED QUANTITY BBL ================= */}

              <div className="mds-info-row highlight">

                <span>
                  Loaded Quantity (BBL)
                </span>

                <span>

                  <input
                        type="number"
                        className="mds-qty-input"

                        value={loadedBBL}

                        onChange={(e) =>
                            setLoadedBBL(
                            Number(e.target.value) || 0
                            )
                        }
                        />

                  <span className="mds-inline-unit">
                    BBL
                  </span>

                </span>

              </div>

              {/* ================= METRIC TONNES ================= */}

              <div className="mds-info-row">

                <span>
                  Loaded Quantity (MT)
                </span>

                <span>

                  {loadedMetricTonnes.toFixed(2)}

                  <span className="mds-inline-unit">
                    MT
                  </span>

                </span>

              </div>

              {/* ================= LITRES ================= */}

              <div className="mds-info-row">

                <span>
                  Loaded Quantity (Litres)
                </span>

                <span>

                  {loadedLitres.toLocaleString(
                    undefined,
                    {
                      maximumFractionDigits: 0
                    }
                  )}

                  <span className="mds-inline-unit">
                    L
                  </span>

                </span>

              </div>

              {/* ================= QUANTITY UNLOADED ================= */}

              <div className="mds-info-row">

                <span>
                  Quantity Unloaded
                </span>

                <span>

                  {quantityUnloaded.toFixed(2)}

                  <span className="mds-inline-unit">
                    BBL
                  </span>

                </span>

              </div>

            </div>

          </div>
        );

      /* =====================================
         CRUDE NAME
      ===================================== */

      case "crudeName":

        return (
          <div className="mds-section">

            <h3>
              CRUDE NAME
            </h3>

            <div className="mds-info-card">

              <div className="mds-info-row">

                <span>
                  Name
                </span>

                <span>
                  {crude.name}
                </span>

              </div>

              <div className="mds-info-row">

                <span>
                  Country
                </span>

                <span>
                  {crude.country}
                </span>

              </div>

              <div className="mds-info-row">

                <span>
                  Type
                </span>

                <span>
                  {crude.type}
                </span>

              </div>

            </div>

          </div>
        );

      /* =====================================
         OTHER TABS
      ===================================== */

      default:

        return (
          <EditableBlock
            title={activeTab.toUpperCase()}

            data={crude[activeTab]}

            isEditing={isEditing}

            setEditableCrude={
              setEditableCrude
            }

            path={activeTab}
          />
        );
    }
  };

  return (


    <Rnd
        bounds="window"
        position={faceplatePos}
          onDragStop={(e,d)=>{
            setFaceplatePos(

                clampPosition(
                    d.x,
                    d.y
                )

            );

        }}
      minWidth={750}
      minHeight={520}

      dragHandleClassName="mds-header"

      className="mds-rnd"
    >

      <div className="mds-faceplate">

        {/* HEADER */}

        <div className="mds-header">

          <div className="mds-title">
            MATERIAL DATA SHEET
          </div>

          <button
            className="mds-close-btn"
            onClick={close}
          >
            ✕
          </button>

        </div>

        {/* CONTROLS */}

        <div className="mds-controls">

          {showBargeSelector && (

        <select
          value={selectedBarge}

          onChange={(e) =>
            setSelectedBarge(
              e.target.value
            )
          }

          className="mds-select"
        >

          <option value="">
            Select Barge
          </option>

          {Object.entries(
            bargeDatabase
          ).map(([k, v]) => (

            <option
              key={k}
              value={k}
            >
              {v.name}
            </option>

          ))}

        </select>

      )}

          <select
            value={selectedCrude}

            onChange={(e) =>
              setSelectedCrude(
                e.target.value
              )
            }

            className="mds-select"
          >

            <option value="">
              Select Crude
            </option>

            {Object.entries(
              crudeDatabase
            ).map(([k, v]) => (

              <option
                key={k}
                value={k}
              >
                {v.name}
              </option>

            ))}

          </select>

          <button
            className={`mds-edit-btn ${
              isEditing ? "active" : ""
            }`}

            onClick={() =>
              setIsEditing(!isEditing)
            }
          >

            {isEditing
              ? "LOCK"
              : "EDIT"}

          </button>

        </div>

        {/* BODY */}

        <div className="mds-body">

          {/* LEFT PANEL */}

          <div className="mds-left-panel">

            {[
              ["quantity", "Quantity"],
              ["crudeName", "Crude Name"],
              ["bulkElementalComposition", "Bulk Element"],
              ["physicalProperties", "Physical"],
              ["corrosionStability", "Corrosion"],
              ["traceMetals", "Metals"],
              ["saraAnalysis", "SARA"],
              ["lightEnds", "Light Ends"],
              ["tbpDistillation", "Distillation"],
              ["cutYields", "Cut Yield"],
              ["residueProperties", "Residue"],
              ["keyPoints", "Key Points"]
            ].map(([k, label]) => (

              <div
                key={k}

                className={`mds-tab ${
                  activeTab === k
                    ? "active"
                    : ""
                }`}

                onClick={() =>
                  setActiveTab(k)
                }
              >

                {label}

              </div>

            ))}

          </div>

          {/* RIGHT PANEL */}

          <div className="mds-right-panel">
            {renderSection()}
          </div>

        </div>

        {/* =========================================================
        DISCLAIMER
        ========================================================= */}

        <div className="mds-disclaimer">

        <strong>DISCLAIMER:</strong>

        The crude oil properties displayed within this Material Data Sheet (MDS) interface
        are intended solely for refinery training, process
        simulation, educational demonstration, and digital twin
        visualization purposes.
            <br />
        These values are compiled from publicly available and
        online reference sources and should not be interpreted
        as certified, laboratory-validated, custody-transfer,
        commercial, or official representations of actual crude
        oil streams, refinery feedstocks, or petroleum products.
            <br />
        NK Refinery DCS and Digital Twin System assumes no
        liability for operational, engineering, commercial,
        or design decisions made using this simulated data.

        </div>


        {/* FOOTER */}

        <div className="mds-footer">

          <div className="mds-footer-title">
            NK Refinery DCS and Digital Twin System
          </div>

          <div className="mds-footer-actions">

            <button className="mds-btn">
              Print
            </button>

            <button className="mds-btn">
              Share
            </button>

            <button className="mds-btn">
              Export
            </button>

          </div>

        </div>

      </div>

    </Rnd>
  );
}

export default MDSFaceplate;